/**
 * GA4 gtag.js bootstrap for the pro admin frontend (KB-2607).
 *
 * Reports to a SEPARATE `G-9D5H9Z4G6G` property — deliberately not
 * marketing's, since pro is an internal ops tool and mixing the two in
 * would pollute marketing's acquisition/conversion reporting.
 *
 * Pro is logged-in-only, so none of the apex's anonymous-queue, consent,
 * or bot-fingerprint machinery applies. `AnalyticsRouteTracker`
 * (src/components/analytics/) is the only caller of `trackPageView` /
 * `setAnalyticsUserId`, and it withholds both until AuthContext resolves
 * a real Firebase uid — there are no anonymous events to guard against
 * here.
 *
 * `window.gtag` is assigned SYNCHRONOUSLY below — before the async
 * gtag.js script has finished loading — so every `dataLayer.push` made
 * before the script arrives still queues. Same pattern as marketing's
 * `GoogleAnalytics.astro` (KB-858/KB-227).
 *
 * `send_page_view: false` — pro is an SPA: gtag's own config-time auto
 * page_view fires once on hard load and never again on client-side
 * navigation, which is a silent undercount. `AnalyticsRouteTracker`'s
 * route-change listener is the single source of `page_view` (mirrors
 * marketing's KB-2491 AC3).
 *
 * No `VITE_GA_MEASUREMENT_ID` ⇒ complete no-op — wip/test/dev builds may
 * run without one (same posture as `sentry-init.ts`).
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export function initGoogleAnalytics(): void {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!measurementId) return;
  if (typeof window === 'undefined') return;
  if (typeof window.gtag === 'function') return; // already armed (HMR / re-import)

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false,
  });
}

/**
 * No-op when GA was never armed (no measurement id configured).
 *
 * `page_title` is passed explicitly (KB-2619) rather than left to GA4's
 * auto-collection, which reads `document.title` on its own schedule and can
 * race a client-side route change — recording the PREVIOUS route's title.
 * Callers must set `document.title` themselves before calling this.
 */
export function trackPageView(pagePath: string, pageTitle: string): void {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', { page_path: pagePath, page_title: pageTitle });
}

/** No-op when GA was never armed (no measurement id configured). */
export function setAnalyticsUserId(uid: string): void {
  if (typeof window.gtag !== 'function') return;
  window.gtag('set', { user_id: uid });
}

/**
 * Generic custom-event sink (KB-2608). `src/lib/analytics/emitEvent.ts` is
 * the only caller — it's what enforces the taxonomy (no raw strings reach
 * here from the rest of the app).
 *
 * No-op when GA was never armed (no measurement id configured).
 */
export function trackEvent(eventName: string, params?: Record<string, string | number | boolean>): void {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params);
}

// Fires as a side effect of merely IMPORTING this module — mirrors
// sentry-init.ts so main.tsx can arm GA the same way it arms Sentry.
initGoogleAnalytics();
